/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import {
    CluProjectCultureSwagger,
    CluProjectDataSwagger,
    CluProjectExportMetaDataSwagger,
    CluProjectImportFormat,
    CluProjectImportProjectSwagger,
    CluProjectSwagger
} from 'src/clu/core/services/projects/cluProjectSwaggers';
import { CluProjectStore } from 'src/clu/core/stores/CluProjectStore';
import { CultureStore } from 'src/core/stores/CultureStore';
import { LuisConstants } from 'src/core/utils/LuisConstants';

export const cluProjectSwaggerToCluProjectStore = (swagger: CluProjectSwagger) =>
    new CluProjectStore(swagger.projectName, {
        culture: swagger.language,
        description: swagger.description,
        isMultiLingual: swagger.multilingual || false,
        oosThreshold: swagger.settings?.confidenceThreshold,
        isCasingNormalized: swagger.settings?.normalizeCasing,
        isDiacriticsAugmented: swagger.settings?.augmentDiacritics,
        createdDate: swagger.createdDateTime && new Date(swagger.createdDateTime),
        modifiedDate: swagger.lastModifiedDateTime && new Date(swagger.lastModifiedDateTime),
        projectType:
            swagger.projectKind?.toLocaleLowerCase() === 'conversation'
                ? 'Conversation'
                : swagger.projectKind?.toLocaleLowerCase() === 'orchestration'
                ? 'Orchestration'
                : swagger.projectKind
    });

export const cluProjectCultureSwaggerToCultureStore = (swagger: CluProjectCultureSwagger) =>
    new CultureStore({ code: swagger.languageCode, name: swagger.languageName });

export const getCluProjectExportMetaDataSwagger = (store: CluProjectStore): CluProjectExportMetaDataSwagger => ({
    stringIndexType: 'Utf16CodeUnit',
    metadata: {
        projectName: store.name,
        language: store.culture,
        projectKind: store.projectType,
        description: store.description,
        multilingual: store.isMultiLingual,
        settings: { confidenceThreshold: store.oosThreshold, normalizeCasing: store.isCasingNormalized, augmentDiacritics: store.isDiacriticsAugmented }
    },
    projectFileVersion: LuisConstants.Clu.AuthoringApiVersion
});

export const getCluProjectImportDataFromFileContent = (fileContent: string): { format: CluProjectImportFormat; projectName: string } => {
    const projectSwagger = <CluProjectImportProjectSwagger>JSON.parse(fileContent);
    if ((<CluProjectDataSwagger>projectSwagger).metadata) {
        return {
            format: 'Conversation',
            projectName: (<CluProjectDataSwagger>projectSwagger).metadata.projectName
        };
    }

    return {
        format: 'Luis',
        projectName: (<{ name: string }>projectSwagger).name
    };
};
